/**
 * 단지 비교함 스토어 — localStorage 기반 (로그인 불요, 호갱노노/직방 표준 UX)
 *
 * 최대 4개 단지를 담아 /compare 에서 나란히 비교한다.
 * React 바인딩은 useCompare() (useSyncExternalStore) 사용.
 * (2026-07-07 경쟁사 격차 해소 로드맵 #10)
 */

const STORAGE_KEY = 'compare_ids';
export const COMPARE_MAX = 4;
const CHANGE_EVENT = 'voonang:compare-change';

function read(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is number => typeof v === 'number').slice(0, COMPARE_MAX);
  } catch {
    return [];
  }
}

function write(ids: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, COMPARE_MAX)));
  } catch {
    // localStorage 불가(사파리 프라이빗 등) — 세션 내 동작만 포기
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

// useSyncExternalStore 요구사항: getSnapshot은 동일 상태에서 동일 참조 반환
let cache: number[] = [];
let cacheKey = '';
export function getCompareIds(): number[] {
  const raw = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '[]';
    } catch {
      return '[]';
    }
  })();
  if (raw !== cacheKey) {
    cacheKey = raw;
    cache = read();
  }
  return cache;
}

/** 담기/빼기 토글. 반환: { ids, added, full } — full=true면 정원 초과로 미추가 */
export function toggleCompare(id: number): { ids: number[]; added: boolean; full: boolean } {
  const ids = getCompareIds();
  if (ids.includes(id)) {
    const next = ids.filter((v) => v !== id);
    write(next);
    return { ids: next, added: false, full: false };
  }
  if (ids.length >= COMPARE_MAX) {
    return { ids, added: false, full: true };
  }
  const next = [...ids, id];
  write(next);
  return { ids: next, added: true, full: false };
}

export function removeCompare(id: number): number[] {
  const next = getCompareIds().filter((v) => v !== id);
  write(next);
  return next;
}

export function clearCompare(): void {
  write([]);
}

/** 변경 구독 (같은 탭 CustomEvent + 다른 탭 storage 이벤트) */
export function subscribeCompare(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}
